<script lang="ts">
    import * as L from 'leaflet';
    import 'leaflet/dist/leaflet.css';
    import { tick } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import {
        COLOR_STRING_ORANGE,
        COLOR_STRING_RED,
        COLOR_STRING_YELLOW,
        formatDuration,
        getLegendColorString
    } from '../utils/style';
    import { encode } from 'html-entities';
    import { giveOffset } from '../utils/offset';
    import { type IIndicatorValue, indicators, type IDeriveOptions } from '../utils/indicators';
    import {
        CongestionTrafficFlowGenerator,
        CoverageTrafficFlowGenerator,
        SpeedTrafficFlowGenerator,
        type ITrafficFlowGenerator
    } from '../utils/trafficFlowGenerators';
    import { notify } from '../utils/notify';
    import { extractRouteCore } from '../utils/routecore';
    import { LatLngExpression } from 'leaflet';
    import {projectStore} from "../stores/project.store";
    import {measurementMomentExpandedStore} from "../stores/measurement-moment-expanded.store";
    import {routeInfoStore} from "../stores/route-info.store";
    import type {Route} from "../types/projectItems/route.type";
    import type {Section} from "../types/projectItems/section.type";
    import type {Gate} from "../types/projectItems/gate.type";
    import type {FlowGate} from "../types/projectItems/flow-gate.type";
    import type {TrafficMeasurement} from "../types/measurements/traffic-measurement.type";
    import type {FlowMeasurement} from "../types/measurements/flow-measurement.type";
    import type {SectionMeasurement} from "../types/measurements/section-measurement.type";
    import type {PolyLineDto} from "../types/dtos/polyline-dto.type";
    import type {CoordinatesDto} from "../types/dtos/coordinate-dto.type";
    import type {CongestionMeasurement} from "../types/measurements/congestion-measurement.type";
    import type {IRouteInfo} from "../utils/types";
    import type {Project} from "../types/project.type";
    import type {MeasurementMomentExpanded} from "../types/expanded-measurement-moment.type";
    import { getAppConfig } from '../helpers/app-config.helper';
    
    const appConfig = getAppConfig();
    const PROFILES = {
        here: {
            url: 'https://{s}.base.maps.ls.hereapi.com/maptile/2.1/maptile/newest/normal.day/{z}/{x}/{y}/512/png?apiKey=FILL_IN',
            subdomains: '1234',
            tileSize: 512,
            zoomOffset: -1
        },
        carto: {
            url: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${appConfig.cartoApiKey}`,
            subdomains: 'abcd',
            tileSize: 512,
            zoomOffset: -1,
            attribution: `&copy;<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>,
            &copy;<a href="https://carto.com/attributions" target="_blank">CARTO</a>`
        }
    };
    const ROUTE_DISTANCE = 8; // Meters between colored lines for routes on the same traject
    const SPEED_DASH = '5';
    const MAPBOX_CONGESTION_LEGEND = {
        Low: [0, 39],
        Moderate: [34, 59],
        Heavy: [60, 79],
        Severe: [80, 100]
    };

    const MAPBOX_LABEL_TRANSLATIONS = {
        Low: 'Licht',
        Moderate: 'Matig',
        Heavy: 'Zwaar',
        Severe: 'Ernstig'
    };

    let mapContainer: HTMLElement;
    let map: L.Map;
    let repaintScheduled = false;
    let mapRouteElements: L.Layer[] = [];
    let hairlines: L.LayerGroup<any>;
    
    let trafficMeasurements = new Map<string, TrafficMeasurement>();
    let flowMeasurements = new Map<string, FlowMeasurement>();
    let sectionMeasurements = new Map<string, SectionMeasurement>();
    let congestionPolyLinesBySeverity = new Map<string, PolyLineDto[]>();

    let firstTimePaint = true;
    
    export let trafficFlowMode: string;
    export let congestionProfile: string[];
    export let indicatorIdx: number;
    export let showSpeedValues: boolean;

    export let showNdwSpeed: boolean;
    export let showNdwFlow: boolean;
    export let modeNdwActive: boolean;
    export let modeNdwInactive: boolean;

    // Important: include every properties that must trigger a repaint in the "$" expression! Otherwise the repaint is not
    // invoked when the property changes.
    $: repaint(
        $projectStore.data,
        $measurementMomentExpandedStore.data,
        $routeInfoStore,
        congestionProfile,
        trafficFlowMode,
        indicatorIdx,
        showSpeedValues,
        showNdwSpeed,
        showNdwFlow,
        modeNdwActive,
        modeNdwInactive
    );

    $: if (true) {
        resizeMap();
    }

    $: parseFlowMeasurements($measurementMomentExpandedStore.data?.flowMeasurements);
    $: parseTrafficMeasurements($measurementMomentExpandedStore.data?.trafficMeasurements); // @TODO
    $: parseSectionMeasurements($measurementMomentExpandedStore.data?.sectionMeasurements);
    $: parseCongestionMeasurements($measurementMomentExpandedStore.data?.congestionMeasurements);

    function mapMounted(container: HTMLElement) {
        map = createMap(container);
        map.on('zoomend', (event: L.LeafletEvent) => {
            repaint(
                $projectStore.data,
                $measurementMomentExpandedStore.data,
                $routeInfoStore,
                congestionProfile,
                trafficFlowMode,
                indicatorIdx,
                showSpeedValues,
                showNdwSpeed,
                showNdwFlow,
                modeNdwActive,
                modeNdwInactive
            );
        });
    }

    function resizeMap() {
        if (map) {
            map.invalidateSize();
        }
    }

    function createMap(container: HTMLElement) {
        const profile = PROFILES.carto;

        let m = L.map(container, {
            zoomSnap: 0.25, // Allow more fluent zooming: not only from one tile level to the next (factor 2), but also in between
            zoomDelta: 0.25,
            preferCanvas: true, // Drastically speeds up rendering by using canvas instead of separate DOM elements per layer,
            zoomControl: false // We add our own zoom control in the bottom left corner
        });
        L.control.zoom({ position: 'bottomleft' }).addTo(m);
        L.tileLayer(profile.url, {
            attribution: profile.attribution ?? '',
            subdomains: profile.subdomains,
            maxZoom: 17,
            tileSize: profile.tileSize,
            zoomOffset: profile.zoomOffset
        }).addTo(m);

        return m;
    }

    // Unused parameters are needed to trigger repaint when they change (other methods refer to the global parameters).
    async function repaint(
        project: Project,
        measurementMomentExpanded: MeasurementMomentExpanded,
        routeInfo: {[key: string]: IRouteInfo},
        congestionProfile: string[],
        trafficFlowMode: string,
        indicatorIdx: number,
        showSpeedValues: boolean,
        showNdwFlow: boolean,
        showNdwSpeed: boolean,
        modeNdwActive: boolean,
        modeNdwInactive: boolean
    ) {
        if (repaintScheduled || $projectStore.loading) {
            return;
        }
        repaintScheduled = true;
        
        await tick();
        addRoutes(trafficFlowMode, indicatorIdx);

        repaintScheduled = false;
    }

    function addHairline(waypoints: CoordinatesDto[]) {
        const waypointTuples: [number, number][] = waypoints.map(
            (waypoint): [number, number] => [
                waypoint.latitude,
                waypoint.longitude
            ]
        );
        
        const hairline = L.polyline(waypointTuples, { color: '#bbb', weight: 2, opacity: 1, lineCap: 'butt' });
        hairline.addTo(hairlines);
    }

    function addPolyline(waypoints: CoordinatesDto[], offset: number, color: string, weight: number, opacity: number, tooltip: string) {
        const waypointTuples: [number, number][] = waypoints.map(
            (waypoint): [number, number] => [
                waypoint.latitude,
                waypoint.longitude
            ]
        );
        
        const offsetted = offset == 0 ? waypointTuples : giveOffset(waypointTuples, offset * ROUTE_DISTANCE, map.getZoom());
        const polyline = L.polyline(offsetted, { color: color, weight: weight, opacity: opacity, dashArray: SPEED_DASH });
        polyline.bindTooltip(tooltip, { sticky: true });
        mapRouteElements.push(polyline);
        polyline.addTo(map);
    }

    function centerMap(gates: Gate[]) {
       
        if (gates && gates.length > 0) {
            const bounds = L.latLngBounds(gates.map((gate) => [gate.coordinatesDto.latitude, gate.coordinatesDto.longitude]));
            map.fitBounds(bounds, {
                paddingTopLeft: [0, 120],
                paddingBottomRight: [0, 40]
            });
        } else {
            // when no gates are present in project, fallback to default -> Vormer location (same as route management map)
            const VORMER_ZWOLLE: LatLngExpression = [52.50846510314036, 6.123970270747168];
            let mapCenter = VORMER_ZWOLLE; // @TODO: is this ok?
            map.setView(mapCenter, 10);
        }
    }

    function addRoutes(trafficFlowMode: string, indicatorIdx: number) {
        if (!map) return;

        clearMap(map);

        if (firstTimePaint) {
            firstTimePaint = false;
            centerMap($projectStore.data?.gates);
        }

        // Traffic flow mode
        // TODO: Pass this object in from InsightFrame
        let flowGenerator: ITrafficFlowGenerator;
        switch (trafficFlowMode) {
            case 'coverage':
                flowGenerator = new CoverageTrafficFlowGenerator();
                break;
            case 'speedhq':
                flowGenerator = new SpeedTrafficFlowGenerator(0.75);
                break;
            case 'speedlq':
                flowGenerator = new SpeedTrafficFlowGenerator(0.71);
                break;
            case 'congestionhq':
                flowGenerator = new CongestionTrafficFlowGenerator(0.75);
                break;
            case 'congestionlq':
                flowGenerator = new CongestionTrafficFlowGenerator(0.71);
                break;
        }

        if (flowGenerator) {
            // @TODO: check if this works correctly
            for (const segment of $projectStore.data?.trafficSegments) {
                const segmentMeasurement = trafficMeasurements.get(segment.id);
                const result = flowGenerator.generate(segment, segmentMeasurement);
                if (!result) continue;
                if (result.offset) {
                    addHairline(segment.polylineDto.coordinates);
                    addPolyline(segment.polylineDto.coordinates, 2 + ($projectStore.data?.routes).length, result.color, result.weight, result.opacity, result.tooltip);
                } else {
                    addPolyline(segment.polylineDto.coordinates, 0, result.color, result.weight, result.opacity, result.tooltip);
                }
            }
        }

        // Congestion mode
        if (congestionProfile.length > 0 && congestionPolyLinesBySeverity) {
           
            const colors = {
                Moderate: COLOR_STRING_YELLOW,
                Heavy: COLOR_STRING_ORANGE,
                Severe: COLOR_STRING_RED
            };
            
            for (const severity of congestionProfile.reverse()) {
                const segments = congestionPolyLinesBySeverity.get(severity);
                if (!segments) {
                    continue;
                }
                const color = colors[severity] ?? '#888888';
                for (const segment of segments) {
                    const waypointTuples: [number, number][] = segment.coordinates.map(
                        (waypoint): [number, number] => [
                            waypoint.latitude,
                            waypoint.longitude
                        ]
                    );
                    
                    const offsetted = giveOffset(waypointTuples, (1 + $projectStore.data?.routes.length) * ROUTE_DISTANCE, map.getZoom());
                    const pl = L.polyline(offsetted, { color, weight: 3, opacity: 1, dashArray: SPEED_DASH });
                    const delay =
                        severity && MAPBOX_CONGESTION_LEGEND[severity]
                            ? `${MAPBOX_CONGESTION_LEGEND[severity][0]}-${MAPBOX_CONGESTION_LEGEND[severity][1]}%`
                            : '-';
                    pl.bindTooltip(`Mapbox: ${encode(MAPBOX_LABEL_TRANSLATIONS[severity])} (${delay}) `, { sticky: true });
                    mapRouteElements.push(pl);
                    pl.addTo(map);
                    addHairline(segment.coordinates);
                }
            }
        }

        $projectStore.data?.routes?.filter(
                route => !$routeInfoStore[route.id]?.hidden
            ).forEach((route) => {
                route.sections?.map((section) => {
                    const values: IIndicatorValue[] = [];
                    const options: IDeriveOptions = {
                        distance: section.distance,
                        actualDuration: sectionMeasurements.get(section.id)?.duration ?? 0,
                        freeFlowDuration: section.freeFlowDuration,
                        typicalDuration: sectionMeasurements.get(section.id)?.typicalDuration ?? 0,
                    };
                    for (const indicator of indicators) {
                        values.push(indicator.derive(options));
                    }
                    renderSection(section, route, values, indicatorIdx);
            });
        });

        for (const gate of $projectStore.data?.gates ?? []) {
            renderGate(gate, map);
        }

        if (modeNdwActive || modeNdwInactive) {
            // Change for every item, so that the chance of having tooltips behind each other reduces.
            let direction = 0;
            const DIRECTIONS: L.Direction[] = ['bottom', 'left', 'right', 'top'];

            for (const fg of ($projectStore.data?.flowGates)) {
                const dir = DIRECTIONS[direction % DIRECTIONS.length];
                renderFlowGate(fg, map, dir);
                direction++;
            }
        }
    }

    function renderSection(section: Section, route: Route, values: IIndicatorValue[], indicatorIdx: number) {
        const haveIndicator = indicatorIdx >= 0;
        const currentIndicator = indicators[indicatorIdx];
        const value = values[indicatorIdx];

        const textValue = currentIndicator?.type === 'duration' ? extractDurationAmount(formatDuration(value?.value)) : (value?.value?.toFixed(0) ?? '-');
        const unitValue = currentIndicator?.type === 'duration' ? extractDurationUnit(formatDuration(value?.value)) : (currentIndicator?.unit ?? '');
        const color = value?.value === undefined ? [127, 127, 127, 1] : value.color;
        const rgba = `rgba(${color[0]},${color[1]},${color[2]},${color[3]})`;

        if (!section.polylineDto) {
            // We do not have polyline data. Just draw a straight dashed line between the gates.
            const gfrom = route.routeGates.find((x) => x.id === section.routeGateFromId).gate;
            const gto = route.routeGates.find((x) => x.id === section.routeGateToId).gate;
            if (gfrom && gto) {
                const waypoints: L.LatLngExpression[] = [
                    [gfrom.coordinatesDto.latitude, gfrom.coordinatesDto.longitude],
                    [gto.coordinatesDto.latitude, gto.coordinatesDto.longitude]
                ];
                const polyline = L.polyline(waypoints, { color: rgba, dashArray: [4] });
                if (haveIndicator) {
                    polyline.bindTooltip(`${encode(textValue)} ${encode(unitValue)}`, { permanent: showSpeedValues, direction: 'center' });
                }
                mapRouteElements.push(polyline);
                polyline.addTo(map);
            }
            return;
        }

        // We have polyline data available which nicely following the roads.
        // Draw it using color coding.
        const origionalWaypoints = section.polylineDto.coordinates;
        const waypoints: [number, number][] = origionalWaypoints.map((p) => [p.latitude, p.longitude]);

        if (!section.polylineDto?.coordinates?.length) {
            console.log('geen geldige polyline');
            return;
        }

        if (haveIndicator) {
            const indicatorPolyline = L.polyline(waypoints, { color: rgba, weight: 5, opacity: 1 });
            // Gray border around the real colored line
            const border = L.polyline(waypoints, { color: '#777', weight: 7, opacity: 0.5 });
            border.addTo(hairlines);
            mapRouteElements.push(indicatorPolyline);
            indicatorPolyline.addTo(map);
        } else {
            addHairline(section.polylineDto.coordinates);
        }

        if (!($routeInfoStore[route.id]?.hidden !== true)) {
            return;
        }

        const routeColor = $routeInfoStore[route.id].color;
        const routergba = `rgba(${routeColor[0]},${routeColor[1]},${routeColor[2]}, ${haveIndicator ? 0.2 : 1.0})`;
        
        let caption = '';
        caption +=
            `<div><span style="display: inline-block; width: 12em;">Route:</span>` +
            `<span style="display: inline-block; width: 2rem; text-align: right;">${encode(route.name)}</span></div>`;
        
        indicators.forEach((ind, idx) => {
            const textValue = ind.type === 'duration' ? extractDurationAmount(formatDuration(values[idx].value)) : (values[idx].value?.toFixed(0) ?? '-');
            const unit = ind.type === 'duration' ? extractDurationUnit(formatDuration(values[idx].value)) : ind.unit;
            const bold = idx === indicatorIdx ? 'font-weight: bold;' : '';
            caption +=
                `<div><span style="display: inline-block; width: 12em; ${bold}">${encode(ind.displayName)}:</span>` +
                `<span style="display: inline-block; width: 2rem; text-align: right;">${encode(textValue)}</span> ${encode(unit)}</div>`;
        });

        const routeIndex = $projectStore.data?.routes.indexOf(route);
        const offset = giveOffset(waypoints, ROUTE_DISTANCE * (1 + routeIndex), map.getZoom());
        const routeLine = L.polyline(offset, { color: routergba, weight: 3, opacity: 1, lineCap: 'butt' });

        if (showSpeedValues) {
            if (haveIndicator) {
                routeLine.bindTooltip(`${encode(textValue)} ${encode(unitValue)}`, { permanent: true, direction: 'center' });
                routeLine.bindPopup(caption);
            } else {
                routeLine.bindTooltip(caption, { sticky: true, direction: 'center' });
            }
        } else {
            routeLine.bindTooltip(caption, { sticky: true, direction: 'center' });
        }
        mapRouteElements.push(routeLine);
        routeLine.addTo(map);
    }

    function renderGate(gate: Gate, map: L.Map) {
        const endRoutes: string[] = [];
        const routes = $projectStore.data?.routes?.filter((route) =>
            route.routeGates?.some((routeGate) => routeGate.gateId === gate.id && $routeInfoStore[route.id]?.hidden !== true)
        );
        routes.forEach((route, i) => {
            const routeGatesOrdered = route.routeGates?.sort((a, b) => a.indexInRoute - b.indexInRoute);
            const radius = (routes.length - i) * 50;
            const color = getLegendColorString($routeInfoStore?.[route.id]?.color);
            const startGate = routeGatesOrdered[0]?.gateId == gate.id;
            const endGate =  routeGatesOrdered[routeGatesOrdered.length - 1]?.gateId == gate.id;
            const marker = L.circle([gate.coordinatesDto.latitude, gate.coordinatesDto.longitude], {
                color,
                radius,
                opacity: startGate || endGate ? 1.0 : 0.1
            });
            const gatekind = startGate ? 'Startgate' : endGate ? 'Endgate' : 'Gate';
            marker.bindTooltip(`${encode(gatekind)} Route ${encode(route.name)} Gate ${encode(gate.name)}`);
            if (startGate || endGate) {
                const core = extractRouteCore(route.name);
                if (!endRoutes.includes(core)) {
                    endRoutes.push(core);
                }
            }
            marker.addTo(map);
            mapRouteElements.push(marker);
        });
        if (endRoutes.length > 0) {
            const tooltip = L.tooltip({ permanent: true, direction: 'top', opacity: 0.7 });
            tooltip.setContent(endRoutes.join('<br>'));
            tooltip.setLatLng([gate.coordinatesDto.latitude, gate.coordinatesDto.longitude]);
            tooltip.addTo(map);
            mapRouteElements.push(tooltip);
        }
    }
    
    function renderFlowGate(flowGate: FlowGate, map: L.Map, dir: L.Direction) {
        const m = flowMeasurements?.get(flowGate.id);
        console.log(m);
        if (!modeNdwInactive && modeNdwActive && !m) return;

        const flow = m?.flow ? m.flow / 60 : undefined;
        const marker = L.circle([flowGate.flowMeasurementSite.coordinatesDto.latitude, flowGate.flowMeasurementSite.coordinatesDto.longitude], {
            radius: 5,
            color: !!m ? '#0000ff' : '#999999'
        });
        marker.bindTooltip(
            // flowGate.Id
            `${encode(flowGate.flowMeasurementSite.externalId)} - 
            ${encode(flowGate.flowMeasurementSite.name)} - 
            ${encode(flowGate.flowMeasurementSite.direction)} - 
            ${encode(m?.speed?.toFixed(0) ?? '-')} `+ $_("measurements.KILOMETERS_PER_HOUR_SHORT") +` - 
            ${encode(flow?.toFixed(0) ?? '-')} `+ $_("measurements.VEHICLES_PER_MINUTE_SHORT")
        );
        marker.addTo(map);
        marker.on('click', async () => {
            await navigator.clipboard.writeText(flowGate.flowMeasurementSite.externalId);
            notify($_("shared.copiedToClipboard", { values: { id: flowGate.flowMeasurementSite.externalId } }));
        });
        mapRouteElements.push(marker);
        // Use opacity 0.5 here to let user see that there may be another tooltip behind. This happens a lot for
        // 2 measurement points on opposite side of the same street. TODO: Make this nicer.
        let label = '';
        if (showNdwSpeed) {
            label = `${encode(m?.speed?.toFixed(0) ?? '-')}` + $_(); // @TODO: check values when correct data in DB
        }
        if (showNdwFlow) {
            if (label.length > 0) {
                label += ' - ';
            }
            label += `${encode(flow?.toFixed(0) ?? '-')} veh/min`; // @TODO: check values when correct data in DB
        }
        if (label) {
            marker.bindTooltip(label, { permanent: true, opacity: 0.5, direction: dir });
        }
    }

    function clearMap(map: L.Map) {
        if (!map) return;

        for (const item of mapRouteElements) {
            item.removeFrom(map);
        }
        mapRouteElements = [];
        hairlines = L.layerGroup();
        mapRouteElements.push(hairlines);
        hairlines.addTo(map);
    }

    function parseTrafficMeasurements(trafficMeasurementsInput?: TrafficMeasurement[]) {
        if (!trafficMeasurementsInput) {
            trafficMeasurements = trafficMeasurements;
            return;
        }

        trafficMeasurements = new Map(
            trafficMeasurementsInput.map((measurement) => [
                measurement.trafficSegmentId,
                measurement
            ])
        );
    }

    function parseSectionMeasurements(sectionMeasurementsInput?: SectionMeasurement[]) {
        if (!sectionMeasurementsInput) {
            sectionMeasurements = sectionMeasurements;
            return;
        }

        sectionMeasurements = new Map(
            sectionMeasurementsInput.map((measurement) => [
                measurement.sectionId,
                measurement
            ])
        );
    }
    
    function parseFlowMeasurements(flowMeasurementsInput?: FlowMeasurement[]) {
        if (!flowMeasurementsInput) {
            flowMeasurements = flowMeasurements;
            return;
        }
        
        flowMeasurements = new Map(
            flowMeasurementsInput.map((measurement) => [
                measurement.flowGateId,
                measurement
            ])
        );
    }

    function parseCongestionMeasurements(congestionMeasurementsInput?: CongestionMeasurement[]) {
        congestionPolyLinesBySeverity = new Map<string, PolyLineDto[]>();

        if (!congestionMeasurementsInput) {
            return;
        }

        for (const measurement of congestionMeasurementsInput) {
            const severity = measurement.congestionLevel;
            const polylines = congestionPolyLinesBySeverity.get(severity) ?? [];

            polylines.push(measurement.polylineDto);
            congestionPolyLinesBySeverity.set(severity, polylines);
        }
    }

    export function extractDurationAmount(value: string) {
        if (value === '-') return '-';
        const nr = value ? parseInt(value) : '';
        return nr.toString();
    }

    export function extractDurationUnit(value: string) {
        if (value === '-') return 's';
        const amount = extractDurationAmount(value);
        return value.slice(amount.length);
    }
</script>

<div bind:this={mapContainer} use:mapMounted class="map" />

<style lang="scss">
    .map {
        width: 100%;
        height: 100%;
        position: relative;
    }
</style>
